import { isProductionEnvironment } from '@luminovo/commons';
import { colorSystem, StickyLayout, TertiaryIconButton, TextField } from '@luminovo/design-system';
import CloseIcon from '@mui/icons-material/Close';
import CommentIcon from '@mui/icons-material/Comment';
import { Box, styled, Tooltip } from '@mui/material';
import React, { createContext, ReactNode, useContext, useEffect, useState } from 'react';

const StyledStickyLayout = styled(StickyLayout)({
    padding: '12px 16px',
    background: colorSystem.neutral[1],
    borderBottom: `1px solid ${colorSystem.neutral[3]}`,
    width: '100%',
    // Make sure it stays on top of other elements
    zIndex: 1200,
});

const ToggleButton = styled(TertiaryIconButton)({
    position: 'fixed',
    bottom: '20px',
    right: '20px',
    backgroundColor: colorSystem.primary[5],
    color: colorSystem.neutral.white,
    '&:hover': {
        backgroundColor: colorSystem.primary[6],
    },
    zIndex: 1200,
    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.2)',
});

interface ScreenRecordingCommentsContextType {
    isVisible: boolean;
    setIsVisible: (visible: boolean) => void;
    comment: string;
    setComment: (comment: string) => void;
}

const ScreenRecordingCommentsContext = createContext<ScreenRecordingCommentsContextType | undefined>(undefined);

export const useScreenRecordingComments = () => {
    const context = useContext(ScreenRecordingCommentsContext);
    if (context === undefined) {
        throw new Error('useScreenRecordingComments must be used within a ScreenRecordingCommentsProvider');
    }
    return context;
};

interface ScreenRecordingCommentsProviderProps {
    children: ReactNode;
}

export const ScreenRecordingCommentsProvider: React.FC<ScreenRecordingCommentsProviderProps> = ({ children }) => {
    const isProduction = isProductionEnvironment();
    const [isVisible, setIsVisible] = useState<boolean>(false);
    const [comment, setComment] = useState<string>('');

    useEffect(() => {
        // Add keyboard shortcut (Alt+C) to toggle visibility
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.ctrlKey && e.key === 'c' && !isProduction) {
                setIsVisible((prev) => !prev);
            }
        };

        if (!isProduction) {
            window.addEventListener('keydown', handleKeyDown);
        }
        return () => {
            if (!isProduction) {
                window.removeEventListener('keydown', handleKeyDown);
            }
        };
    }, [isProduction]);

    const value = {
        isVisible,
        setIsVisible,
        comment,
        setComment,
    };

    return <ScreenRecordingCommentsContext.Provider value={value}>{children}</ScreenRecordingCommentsContext.Provider>;
};

interface ScreenRecordingCommentsBoxProps {
    visible?: boolean;
}

export const ScreenRecordingCommentsBox: React.FC<ScreenRecordingCommentsBoxProps> = ({ visible: propVisible }) => {
    const { isVisible, setIsVisible, comment, setComment } = useScreenRecordingComments();
    const visible = propVisible !== undefined ? propVisible : isVisible;

    return (
        <>
            {visible && (
                <StyledStickyLayout>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TextField
                            fullWidth
                            size="small"
                            id="screen-recording-comments"
                            placeholder="Add comments for screen recording..."
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                            inputProps={{
                                'aria-label': 'Screen recording comments',
                            }}
                        />
                        <Tooltip title="Close comments box (CTRL+C)">
                            <TertiaryIconButton
                                onClick={() => setIsVisible(false)}
                                size="small"
                                aria-label="Close comments box"
                            >
                                <CloseIcon />
                            </TertiaryIconButton>
                        </Tooltip>
                    </Box>
                </StyledStickyLayout>
            )}

            {!visible && (
                <Tooltip title="Show recording comments (CTRL+C)">
                    <ToggleButton onClick={() => setIsVisible(true)} size="medium" aria-label="Show recording comments">
                        <CommentIcon />
                    </ToggleButton>
                </Tooltip>
            )}
        </>
    );
};
